/**
 * Object utility functions to replace lodash
 */

/**
 * Deep equality check for objects and arrays
 * Replaces: _.isEqual(a, b)
 */
export function isEqual(a: any, b: any): boolean {
  if (a === b) return true;
  
  if (a == null || b == null) return false;
  
  if (typeof a !== typeof b) return false;
  
  if (typeof a !== 'object') return a === b;
  
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  
  if (Array.isArray(a)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!isEqual(a[i], b[i])) return false;
    }
    return true;
  }
  
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  
  if (keysA.length !== keysB.length) return false;
  
  for (const key of keysA) {
    if (!keysB.includes(key)) return false;
    if (!isEqual(a[key], b[key])) return false;
  }
  
  return true;
}

/**
 * Omit specified keys from an object
 * Replaces: _.omit(object, ['key1', 'key2'])
 */
export function omit<T extends Record<string, any>, K extends keyof T>(
  obj: T,
  keys: K[] | K
): Omit<T, K> {
  const keysToOmit = Array.isArray(keys) ? keys : [keys];
  const result = { ...obj };
  
  for (const key of keysToOmit) {
    delete result[key];
  }
  
  return result;
}

/**
 * Pick properties from an object based on a condition
 * Replaces: _.pickBy(object, (value) => value !== undefined)
 */
export function pickBy<T extends Record<string, any>>(
  obj: T,
  predicate: (value: any, key: string) => boolean
): Partial<T> {
  const result: Partial<T> = {};
  
  for (const key in obj) {
    if (obj.hasOwnProperty(key) && predicate(obj[key], key)) {
      result[key] = obj[key];
    }
  }
  
  return result;
}

/**
 * Deep merge objects
 * Replaces: _.merge(target, source1, source2, ...)
 */
export function merge<T extends Record<string, any>>(target: T, ...sources: Partial<T>[]): T {
  if (!sources.length) return target;
  
  const result = { ...target };
  
  for (const source of sources) {
    if (!source || typeof source !== 'object') continue;
    
    for (const key in source) {
      if (!source.hasOwnProperty(key)) continue;
      
      const sourceValue = source[key];
      const targetValue = result[key];
      
      if (isPlainObject(sourceValue) && isPlainObject(targetValue)) {
        result[key] = merge(targetValue, sourceValue);
      } else {
        result[key] = sourceValue as any;
      }
    }
  }
  
  return result;
}

/**
 * Check if value is a plain object (not array, not null)
 */
function isPlainObject(value: any): value is Record<string, any> {
  return value !== null && 
         typeof value === 'object' && 
         !Array.isArray(value) &&
         Object.prototype.toString.call(value) === '[object Object]';
}

/**
 * Safely finds an item in a collection (array or object)
 * Returns undefined if collection is null/undefined or item not found.
 * Replaces: _.find(collection, predicate)
 */
export function safeFind<T>(
  collection: T[] | Record<string, T> | null | undefined,
  predicate: (value: T, index?: number | string, collection?: T[] | Record<string, T>) => boolean
): T | undefined {
  if (collection == null) {
    return undefined;
  }

  if (Array.isArray(collection)) {
    return collection.find(predicate);
  } else if (typeof collection === 'object') {
    for (const key in collection) {
      if (Object.prototype.hasOwnProperty.call(collection, key)) {
        const value = collection[key];
        if (predicate(value, key, collection)) {
          return value;
        }
      }
    }
  }
  return undefined;
}

/**
 * Safely finds the index of an item in a collection (array or object)
 * Returns -1 if collection is null/undefined or item not found.
 * Replaces: _.findIndex(collection, predicate)
 */
export function safeFindIndex<T>(
  collection: T[] | Record<string, T> | null | undefined,
  predicate: (value: T, index?: number | string, collection?: T[] | Record<string, T>) => boolean
): number {
  if (collection == null) {
    return -1;
  }

  if (Array.isArray(collection)) {
    return collection.findIndex(predicate);
  } else if (typeof collection === 'object') {
    let index = 0;
    for (const key in collection) {
      if (Object.prototype.hasOwnProperty.call(collection, key)) {
        const value = collection[key];
        if (predicate(value, key, collection)) {
          return index;
        }
        index++;
      }
    }
  }
  return -1;
}
